import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Heart, Sparkles, Star, Phone, Baby } from 'lucide-react';
import { testimonials, services, benefits } from '../mock';
import GetStartedForm from '../components/GetStartedForm';

const EnergyTherapy = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  if (showForm) {
    return <GetStartedForm onClose={() => setShowForm(false)} />;
  }

  return (
    <div className="landing-page">
      {/* Header */}
      <header className="header-nav">
        <div className="header-content">
          <Link to="/" data-testid="et-logo-link">
            <img src="/brand/logo.png" alt="Momshiach — Your Personal Redemption" className="logo-img" />
          </Link>
          <nav className="desktop-nav">
            <Link to="/" className="nav-link" data-testid="et-nav-home">Home</Link>
            <button onClick={() => scrollToSection('about')} className="nav-link">About</button>
            <button onClick={() => scrollToSection('how-it-works')} className="nav-link">How It Works</button>
            <button onClick={() => scrollToSection('benefits')} className="nav-link">Benefits</button>
            <button onClick={() => scrollToSection('testimonials')} className="nav-link">Testimonials</button>
          </nav>
          <div className="nav-actions">
            <a href="https://wa.me/972529326458" target="_blank" rel="noopener noreferrer" className="btn-secondary btn-contact">
              <Phone size={16} />
              <span>Contact</span>
            </a>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="mobile-menu-btn">
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="mobile-menu">
            <Link to="/" className="mobile-nav-link">Home</Link>
            <button onClick={() => scrollToSection('about')} className="mobile-nav-link">About</button>
            <button onClick={() => scrollToSection('how-it-works')} className="mobile-nav-link">How It Works</button>
            <button onClick={() => scrollToSection('benefits')} className="mobile-nav-link">Benefits</button>
            <button onClick={() => scrollToSection('testimonials')} className="mobile-nav-link">Testimonials</button>
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-announcement">
            <Sparkles size={14} />
            <span>No Time Commitment • Remote Sessions • Work While You Sleep</span>
          </div>
          <p className="hero-tagline" data-testid="et-hero-tagline">Your Personal Redemption</p>
          <h1 className="heading-hero">You love your kids.<br />You just miss yourself.</h1>
          <p className="hero-subtitle body-large">
            Momshiach Kosher Energy Therapy — a gentle, values-aligned emotional and energetic process that helps women release stress, clear blockages, and reconnect to inner balance within a modest, kosher framework.
          </p>
          <div className="hero-callout">
            <p className="body-medium"><strong>The beauty of energy work:</strong> You don't need to be present during the session. No Zoom calls, no time blocked out of your day. Healing happens remotely while you go about your routine - even while you sleep.</p>
          </div>
          <div className="hero-cta-group">
            <button onClick={() => setShowForm(true)} className="btn-primary btn-lg" data-testid="et-start-journey-btn">Start Your Journey</button>
            <button onClick={() => scrollToSection('about')} className="btn-secondary btn-lg">Learn More</button>
          </div>
          <div className="et-first-time-badge" data-testid="et-first-time-badge">
            <Sparkles size={14} />
            <span><strong>First-time client?</strong> Save 10% on your intake — <s>3,570 NIS</s> <strong>3,213 NIS</strong>. Auto-applied at checkout.</span>
          </div>
        </div>
      </section>

      {/* Passive Therapy */}
      <section className="passive-therapy-section">
        <div className="container">
          <div className="passive-therapy-card">
            <h2 className="heading-2">Healing Without the Time Commitment</h2>
            <p className="body-large">
              As a busy mom, finding time for yourself feels impossible. That's why energy therapy is different.
            </p>
            <div className="passive-features">
              <div className="passive-feature">
                <div className="feature-icon">✓</div>
                <div>
                  <h3 className="heading-3">No Zoom Calls Required</h3>
                  <p className="body-small">Energy work does not require any personal meeting or formal interaction before, during or after the actual session.</p>
                </div>
              </div>
              <div className="passive-feature">
                <div className="feature-icon">✓</div>
                <div>
                  <h3 className="heading-3">Zero Time Commitment</h3>
                  <p className="body-small">On the day of your session, you don't need to be personally available at any time or change your day's routine. Go to work, care for your kids, even sleep.</p>
                </div>
              </div>
              <div className="passive-feature">
                <div className="feature-icon">✓</div>
                <div>
                  <h3 className="heading-3">Receive Audio Report After</h3>
                  <p className="body-small">Sessions take about an hour to complete. After the energy session, you'll receive an audio recording with comprehensive findings via email or WhatsApp.</p>
                </div>
              </div>
            </div>
            <p className="body-medium passive-emphasis">
              <strong>This is truly passive healing:</strong> The energetic work happens remotely while the light of Hashem flows through Chanah. You simply receive the benefits without needing to pause your life.
            </p>

            <div className="kids-callout" data-testid="et-kids-callout">
              <h3 className="heading-3">
                <Baby size={22} color="var(--brand-gold)" />
                A Gentle Way to Help Your Kids — Without the Fight
              </h3>
              <p className="body-medium">
                Because the client doesn't need to be present for the session, this is also a beautiful way for parents to give their kids therapy <strong>without their kids putting up a fight</strong>. No coaxing them into a therapist's office. No fighting through a sit-down session. You simply arrange the energy work — and they receive the healing as they go about their day.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About */}
      <section id="about" className="content-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Momshiach is not a method. It is a derech — a way back to yourself.</h2>
          </div>
          <div className="info-card glass-card">
            <p className="body-medium">
              Most wellness programs work only on the body or the mind. At Momshiach, healing happens at the root. We work with the trapped emotions, traumas, fears, and blockages that drive patterns in your health, emotions, relationships, and energy, while also giving you grounded tools to live differently in the real world.
            </p>
            <p className="body-medium">
              Our approach is rare because it is fully integrated: <strong>Kosher Energy Therapy</strong> clears what is stuck beneath the surface. <strong>EFT Tapping & Narrative Therapy</strong> help you process and rewire what comes up. <strong>Integrative Nutrition & cooking support</strong> bring the healing into your body and daily life.
            </p>
            <p className="body-medium">
              This means you are not just "understanding" your issues — you are actually shifting them. From a Breslov lens, this is tikun: gently uncovering the places where the soul has been carrying weight that was never meant to be held. When energy is cleared, a person naturally returns to clarity, vitality, and simcha.
            </p>
            <p className="body-medium">
              <strong>You don't need to become someone new. You just need the layers in the way to be lifted.</strong> And that is what Momshiach is here to do.
            </p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="content-section bg-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">How It Works</h2>
            <p className="body-large text-secondary">Your path to healing in 5 simple steps</p>
          </div>
          <div className="steps-grid">
            <div className="step-card"><div className="step-number">1</div><h3 className="heading-3">Complete Questionnaire</h3><p className="body-medium">Fill out a comprehensive questionnaire to help us understand your specific needs and intentions for healing.</p></div>
            <div className="step-card"><div className="step-number">2</div><h3 className="heading-3">Submit Photo</h3><p className="body-medium">Provide a full-length photo from the last 3 months of the person receiving the energy work.</p></div>
            <div className="step-card"><div className="step-number">3</div><h3 className="heading-3">Sign Agreement</h3><p className="body-medium">Review and sign the consent form and waiver to begin your healing journey.</p></div>
            <div className="step-card"><div className="step-number">4</div><h3 className="heading-3">Submit Payment</h3><p className="body-medium">Secure payment for 6 sessions package (3,570 NIS) via credit card.</p></div>
            <div className="step-card"><div className="step-number">5</div><h3 className="heading-3">Receive Healing Remotely</h3><p className="body-medium">Sessions happen without you needing to be present! No Zoom calls, no appointments to keep. Continue your daily routine - the energy work happens remotely while you go about your day or even sleep. You'll receive an audio report after each session.</p></div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section id="benefits" className="content-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Benefits of Energy Therapy</h2>
            <p className="body-large text-secondary">Experience transformation in multiple areas of your life</p>
          </div>
          <div className="benefits-grid">
            {benefits.map((benefit) => (
              <div key={benefit.id} className="benefit-card">
                <Heart className="benefit-icon" size={24} />
                <h3 className="heading-3">{benefit.title}</h3>
                <p className="body-small">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Complementary Services */}
      <section id="services" className="content-section bg-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Complementary Modalities</h2>
            <p className="body-large text-secondary">Included in the Momshiach approach</p>
          </div>
          <div className="services-grid">
            {services.map((service) => (
              <div key={service.id} className="service-card">
                <Sparkles className="service-icon" size={20} />
                <h3 className="heading-3">{service.name}</h3>
                <p className="body-small">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Video Testimonials */}
      <section id="video-testimonials" className="content-section bg-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Watch Their Stories</h2>
            <p className="body-large text-secondary">Real women. Real shifts. In their own words.</p>
          </div>
          <div className="et-video-grid">
            {[
              "1NJnKDjYg0elNJRgvX5IxILSQb46MxYm6",
              "1EeHo9aJLhv5Huud4innGBtr7viwTzuL3",
              "1JPaJ9bwqumi1-OlOITwbIQ7mgwiGWhPC",
              "1YG3nNKxjXaS8nl9M5kRAPw6l-2HWAVCJ",
            ].map((vid, i) => (
              <div key={vid} className="et-video-card">
                <div className="video-embed">
                  <iframe
                    src={`https://drive.google.com/file/d/${vid}/preview`}
                    allow="autoplay"
                    allowFullScreen
                    title={`Energy Therapy Testimonial ${i + 1}`}
                    data-testid={`et-video-testimonial-${i + 1}`}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="content-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Real Struggles, Real Results</h2>
            <p className="body-large text-secondary">Hear from women who have experienced transformation</p>
          </div>
          <div className="testimonials-grid">
            {testimonials.map((t) => (
              <div key={t.id} className="testimonial-card">
                <div className="stars">
                  {[...Array(t.rating)].map((_, i) => <Star key={i} size={16} fill="currentColor" />)}
                </div>
                <p className="body-medium testimonial-text">"{t.text}"</p>
                <div className="testimonial-author">
                  <p className="body-small"><strong>{t.name}</strong></p>
                  {t.location && <p className="caption">{t.location}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="heading-1">Come back to yourself — and your motherhood will breathe again</h2>
            <p className="body-large">When energy flows freely, the body and soul return to a state of harmony and clarity. It's a gentle, powerful way to support deep healing from within.</p>
            <button onClick={() => setShowForm(true)} className="btn-primary btn-lg" data-testid="et-cta-begin-btn">Begin Your Journey</button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-bottom">
            <p className="caption">© 2025 Momshiach · Kosher Energy Therapy · Techniques taught under Rabbi Ben-Tzion Wosner shlit"a.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default EnergyTherapy;
